import { Box, Typography } from "@mui/joy";
import { Hash } from "lucide-react";
import { useFormContext } from "react-hook-form";

import { FormInput } from "../../../shared";
import { ROOF_MEASUREMENT_TYPES } from "../../../constants/roofMeasurements";
import type { RoofMeasurementKey } from "../../../interfaces";

function formatValue(value: number) {
    if (!value) return "—";
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

export function RoofMeasurements({ loading }: { loading?: boolean }) {
    const { watch } = useFormContext();
    const measurements = watch("Measurements") || {};

    return (
        <Box>
            <Box mb={2}>
                <Typography level="title-md">
                    Roof Measurements
                </Typography>
                <Typography level="body-sm" color="neutral">
                    Enter actual field measurements. Calculated values are shown
                    for reference.
                </Typography>
            </Box>

            <Box
                sx={{
                    p: 2,
                    borderRadius: "4px",
                    border: "1px solid",
                    borderColor: "rgba(128, 128, 128, 0.2)",
                    bgcolor: "rgba(128, 128, 128, 0.05)",
                    backdropFilter: "blur(12px)",
                }}
            >
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, 1fr)",
                            lg: "repeat(3, 1fr)",
                        },
                        gap: 2.5,
                    }}
                >
                    {ROOF_MEASUREMENT_TYPES.map(({ key, label, unit }) => {
                        const measurementKey = key as RoofMeasurementKey;
                        const calculated =
                            Number(measurements[measurementKey]?.Calculated) ||
                            0;
                        const actual =
                            Number(measurements[measurementKey]?.Actual) || 0;
                        const difference = actual - calculated;
                        const hasDifference = actual > 0 && difference !== 0;

                        return (
                            <Box key={key} sx={{ minWidth: 0 }}>
                                <Typography
                                    level="body-xs"
                                    sx={{
                                        textTransform: "uppercase",
                                        fontWeight: "xl",
                                        letterSpacing: "0.5px",
                                    }}
                                >
                                    {label} ({unit})
                                </Typography>
                                <Typography
                                    level="body-xs"
                                    color="neutral"
                                    sx={{ mt: -0.5, mb: 0.5, fontSize: "12px" }}
                                >
                                    Calculated: {formatValue(calculated)}
                                    {hasDifference && (
                                        <Typography
                                            component="span"
                                            level="body-xs"
                                            sx={{
                                                ml: 0.75,
                                                color:
                                                    difference > 0
                                                        ? "warning.500"
                                                        : "primary.500",
                                                fontWeight: "md",
                                            }}
                                        >
                                            · Diff {difference > 0 ? "+" : ""}
                                            {formatValue(difference)}
                                        </Typography>
                                    )}
                                </Typography>
                                <FormInput
                                    name={`Measurements[${measurementKey}].Actual`}
                                    type="number"
                                    placeholder="0"
                                    helperText={`Actual measurement in ${unit}.`}
                                    disabled={loading}
                                    startDecorator={<Hash size={16} />}
                                />
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
}
